import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Check, Link2, Calendar, Shield, Loader2, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getShareUrl } from "@/lib/shareUrl";
import { useUsageLimits } from "@/hooks/useUsageLimits";
import { LimitReached } from "@/components/usage/UpgradePrompt";
import type { Tables } from "@/integrations/supabase/types";

type Document = Tables<"documents">;

interface ShareDialogProps {
  document: Document | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShareDialog({ document, open, onOpenChange }: ShareDialogProps) {
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [enableExpiration, setEnableExpiration] = useState(false);
  const [expirationDays, setExpirationDays] = useState("7");
  const [permission, setPermission] = useState<"read" | "read_comment">("read");
  const [sendEmail, setSendEmail] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  
  const { usage, limits, checkLimit, refreshUsage } = useUsageLimits();
  const canShare = checkLimit("share_links");

  // Refresh usage when dialog opens
  useEffect(() => {
    if (open) {
      refreshUsage();
    }
  }, [open, refreshUsage]);

  const generateShareLink = async () => {
    if (!document) return;

    // Check limit before creating
    if (!canShare) {
      toast.error("Limite de liens de partage atteinte");
      return;
    }

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Vous devez être connecté");
        return;
      }

      const expiresAt = enableExpiration
        ? new Date(Date.now() + parseInt(expirationDays) * 24 * 60 * 60 * 1000).toISOString()
        : null;

      const { data, error } = await supabase
        .from("share_links")
        .insert({
          document_id: document.id,
          user_id: user.id,
          permission,
          expires_at: expiresAt,
        })
        .select()
        .single();

      if (error) throw error;

      const url = getShareUrl(data.token);
      setShareUrl(url);

      // Send email notification if enabled
      if (sendEmail && recipientEmail) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", user.id)
          .single();

        await supabase.functions.invoke("notify-share", {
          body: {
            recipientEmail,
            recipientName: recipientName || undefined,
            documentTitle: document.title,
            shareUrl: url,
            senderName: profile?.full_name || undefined,
            permission,
            expiresAt: expiresAt || undefined,
          },
        });
        toast.success("Lien créé et notification envoyée");
      } else {
        toast.success("Lien de partage créé");
      }
      
      // Refresh usage to update share_links count
      refreshUsage();
    } catch (error: any) {
      console.error("Error creating share link:", error);
      toast.error("Erreur lors de la création du lien");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!shareUrl) return;

    let copied = false;
    
    // Try native clipboard API first
    try {
      await navigator.clipboard.writeText(shareUrl);
      copied = true;
    } catch {
      // Fallback: create a temporary textarea
      try {
        const textarea = window.document.createElement('textarea');
        textarea.value = shareUrl;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        textarea.setAttribute('readonly', '');
        window.document.body.appendChild(textarea);
        textarea.select();
        textarea.setSelectionRange(0, 99999); // For mobile
        window.document.execCommand('copy');
        window.document.body.removeChild(textarea);
        copied = true;
      } catch {
        copied = false;
      }
    }

    if (copied) {
      setCopied(true);
      toast.success("Lien copié dans le presse-papier");
      setTimeout(() => setCopied(false), 2000);
    } else {
      // Show the URL in a toast so user can manually copy
      toast.info("Copiez ce lien :", {
        description: shareUrl,
        duration: 10000,
      });
    }
  };

  const handleClose = () => {
    setShareUrl(null);
    setCopied(false);
    setEnableExpiration(false);
    setExpirationDays("7");
    setPermission("read");
    setSendEmail(false);
    setRecipientEmail("");
    setRecipientName("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Partager le document
          </DialogTitle>
          <DialogDescription>
            {document?.title}
          </DialogDescription>
        </DialogHeader>

        {!shareUrl ? (
          !canShare ? (
            <div className="py-4">
              <LimitReached 
                type="share_links" 
                current={usage.share_links} 
                limit={limits.share_links} 
              />
            </div>
          ) : (
          <div className="space-y-6 py-4">
            {/* Permission */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Permission
              </Label>
              <Select value={permission} onValueChange={(v) => setPermission(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="read">Lecture seule</SelectItem>
                  <SelectItem value="read_comment">Lecture + Commentaires</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Expiration */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="expiration" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date d'expiration
                </Label>
                <Switch
                  id="expiration"
                  checked={enableExpiration}
                  onCheckedChange={setEnableExpiration}
                />
              </div>
              {enableExpiration && (
                <Select value={expirationDays} onValueChange={setExpirationDays}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 jour</SelectItem>
                    <SelectItem value="7">7 jours</SelectItem>
                    <SelectItem value="30">30 jours</SelectItem>
                    <SelectItem value="90">90 jours</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Email notification */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="sendEmail" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Notifier par email
                </Label>
                <Switch
                  id="sendEmail"
                  checked={sendEmail}
                  onCheckedChange={setSendEmail}
                />
              </div>
              {sendEmail && (
                <div className="space-y-3">
                  <Input
                    type="email"
                    placeholder="Email du destinataire *"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                  />
                  <Input
                    type="text"
                    placeholder="Nom du destinataire (optionnel)"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                  />
                </div>
              )}
            </div>

            <Button 
              onClick={generateShareLink} 
              disabled={loading || (sendEmail && !recipientEmail)} 
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Génération...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4 mr-2" />
                  {sendEmail ? "Générer et envoyer" : "Générer le lien"}
                </>
              )}
            </Button>
          </div>
          )
        ) : (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2">
              <Input
                value={shareUrl}
                readOnly
                className="flex-1 font-mono text-sm"
              />
              <Button variant="outline" size="icon" onClick={copyToClipboard}>
                {copied ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              {enableExpiration
                ? `Ce lien expire dans ${expirationDays} jour(s).`
                : "Ce lien n'expire pas."}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose} className="flex-1">
                Fermer
              </Button>
              <Button onClick={() => setShareUrl(null)} className="flex-1">
                Nouveau lien
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
